import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useQueries, useQuery } from "@tanstack/react-query";
import { ArrowRight, Check, Mail } from "lucide-react";
import { GLOBAL_IMAGES } from "../assets/asset";
import {
  extractTeacherRecord,
  getTeacherDisplayName,
  getTeacherEmail,
  getTeacherIdentifier,
  getTeacherPhotoValue,
  normalizeAvailabilityCollection,
  normalizeTeacherCollection,
  teacherApi,
} from "../features/teacher/teacherApi";
import { logout } from "../features/auth/user.reducer";
import { formatTeacherYearLabels } from "../utils/teacherYears";
import { normalizeAudPriceForDisplay } from "../utils/audCurrency";
import { resolveApiImageUrl } from "../utils/imageUrl";
import { getStaticTutorDescription } from "./tutorDescriptions";
import { logTutorProfileClicked } from "../utils/datadogLogger";
import LogoutConfirmDialog from "../components/LogoutConfirmDialog";
import { mergePendingTutorSlotsFromStorage } from "../components/ClassBooking/pendingTutorAvailability";

const PUBLIC_FILTER_OPTIONS = [
  { id: "verified-tutors", label: "Verified Tutors" },
  {
    id: "affordable-options",
    label: "Affordable Options for Every Budget",
  },
  {
    id: "curriculum-aligned-learning",
    label: "Curriculum-Aligned Learning",
  },
  {
    id: "flexible-parent-friendly",
    label: "Flexible & Parent-Friendly",
  },
];

const QUICK_RESPONSE_LABEL = "Quick to respond";
const PUBLIC_TUTOR_QUERY_STALE_TIME_MS = 5 * 60 * 1000;
const PUBLIC_TUTOR_LIST_FALLBACK_ERROR_MESSAGE =
  "We couldn't load tutors right now. Please check your internet and try again.";
const FREE_TRIAL_BOOKING_URL = "https://calendar.app.google/sQBNmtkZLP2nQ4Hn9";
const FREE_TRIAL_BOOKING_STORAGE_PREFIX = "mathdingo:book-free-trial-used";

const TUTOR_YEAR_VALUE_KEYS = [
  "name",
  "title",
  "label",
  "year",
  "standard",
  "id",
  "value",
];

const splitTutorYearValues = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => splitTutorYearValues(item));
  }

  if (value && typeof value === "object") {
    const matchedValue = TUTOR_YEAR_VALUE_KEYS.map((key) => value[key]).find(
      (candidate) => String(candidate || "").trim()
    );
    return matchedValue ? splitTutorYearValues(matchedValue) : [];
  }

  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const formatTutorYearValue = (value) => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) return "";

  if (/^foundation$/i.test(normalizedValue)) {
    return "Foundation";
  }

  const matchedYear = normalizedValue.match(/^year\s*(\d+)$/i);
  if (matchedYear) {
    return `Year ${Number(matchedYear[1])}`;
  }

  if (/^\d+$/.test(normalizedValue)) {
    return `Year ${Number(normalizedValue)}`;
  }

  return normalizedValue;
};

const parseSlotDate = (value) => {
  if (!value) return null;
  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const isFutureAvailabilitySlot = (slot, now = new Date()) => {
  const startDate = parseSlotDate(slot?.start_time || slot?.startTime);
  if (!startDate) return false;
  return startDate.getTime() > now.getTime();
};

const getTeacherYearSource = (teacher = {}) =>
  [
    teacher?.year_labels,
    teacher?.year,
    teacher?.years,
    teacher?.years_taught,
    teacher?.yearsTaught,
    teacher?.yearIds,
    teacher?.year_id,
    teacher?.yearId,
  ].find((candidate) =>
    Array.isArray(candidate)
      ? candidate.length > 0
      : String(candidate || "").trim().length > 0
  );

const getTeacherYears = (teacher = {}) => {
  const yearSource = getTeacherYearSource(teacher);

  const formattedYears = formatTeacherYearLabels(yearSource);
  if (formattedYears.length) {
    return formattedYears;
  }

  return Array.from(
    new Set(
      splitTutorYearValues(yearSource)
        .map((item) => formatTutorYearValue(item))
        .filter(Boolean)
    )
  );
};

const getInitials = (name = "Teacher") =>
  String(name || "Teacher")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "T";

const formatPriceLabel = (price) => {
  if (typeof price !== "number" || Number.isNaN(price)) {
    return "Price on request";
  }

  const formatter = new Intl.NumberFormat("en-AU", {
    minimumFractionDigits: Number.isInteger(price) ? 0 : 2,
    maximumFractionDigits: Number.isInteger(price) ? 0 : 2,
  });

  return `$${formatter.format(price)} / h`;
};

const getFreeTrialBookingStorageKey = (user) => {
  const userIdentifier =
    user?.UserId || user?.user_id || user?.id || user?.pk || user?.email || "guest";

  return `${FREE_TRIAL_BOOKING_STORAGE_PREFIX}:${String(userIdentifier)}`;
};

const getUserIdentifier = (user = {}) =>
  user?.UserId || user?.user_id || user?.id || user?.pk || null;

const getUserDisplayName = (user = {}) =>
  [
    user?.name,
    user?.user_name,
    user?.full_name,
    user?.fullName,
    user?.first_name,
    user?.firstName,
  ]
    .map((value) => String(value || "").trim())
    .find(Boolean) || "Guest";

const hasUsedFreeTrialBooking = (user) => {
  try {
    return localStorage.getItem(getFreeTrialBookingStorageKey(user)) === "true";
  } catch (_error) {
    return false;
  }
};

const markFreeTrialBookingUsed = (user) => {
  try {
    localStorage.setItem(getFreeTrialBookingStorageKey(user), "true");
  } catch (_error) {
    // Storage can fail in private browsing; the redirect should still work.
  }
};

const shouldRetryPublicTutorQuery = (failureCount, error) => {
  if (failureCount >= 1) return false;
  if (!error || typeof error !== "object") return false;

  if (error.isTimeout || error.isNetworkError) return true;

  const status = Number(error.status);
  if (status === 0) return true;
  return status >= 500 && status <= 599;
};

const getPublicTutorListErrorMessage = (error) => {
  const message = String(error?.message || "").trim();
  if (!message) return PUBLIC_TUTOR_LIST_FALLBACK_ERROR_MESSAGE;

  if (/timeout|timed out|network error|failed to fetch|load failed/i.test(message)) {
    return PUBLIC_TUTOR_LIST_FALLBACK_ERROR_MESSAGE;
  }

  return message;
};

const buildTutorCardModel = (teacher, index, options = {}) => {
  const name = getTeacherDisplayName(teacher);
  const years = getTeacherYears(teacher);
  const rawPrice = teacher?.price_per_session ?? teacher?.price;
  const price = normalizeAudPriceForDisplay(rawPrice);
  const photo = resolveApiImageUrl(getTeacherPhotoValue(teacher));
  const description =
    String(teacher?.bio || "").trim() || getStaticTutorDescription(index);

  return {
    id: getTeacherIdentifier(teacher) || `teacher-${index}`,
    routeId: getTeacherIdentifier(teacher),
    name,
    email: getTeacherEmail(teacher),
    years,
    initials: getInitials(name),
    image: photo,
    price,
    responseTime: QUICK_RESPONSE_LABEL,
    description,
    hasAvailableSlots: Boolean(options.hasAvailableSlots),
  };
};

export default function TutorInfoPage() {
  const dispatch = useDispatch();
  const { token, user } = useSelector((state) => state.user);
  const [showAllTutors, setShowAllTutors] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showTutorEnquiryPopup, setShowTutorEnquiryPopup] = useState(false);
  const [hasUsedFreeTrial, setHasUsedFreeTrial] = useState(() =>
    hasUsedFreeTrialBooking(user)
  );

  useEffect(() => {
    setHasUsedFreeTrial(hasUsedFreeTrialBooking(user));
  }, [user]);

  const teacherListQuery = useQuery({
    queryKey: ["public-tutor-info"],
    queryFn: () => teacherApi.getAllTeachers(),
    retry: shouldRetryPublicTutorQuery,
    staleTime: PUBLIC_TUTOR_QUERY_STALE_TIME_MS,
  });

  const rawTeachers = useMemo(
    () =>
      (normalizeTeacherCollection(teacherListQuery.data) || []).map((teacher, index) =>
        extractTeacherRecord(teacher, teacher || {}) || teacher || { id: `teacher-${index}` }
      ),
    [teacherListQuery.data]
  );

  const tutorDetailQueries = useQueries({
    queries: rawTeachers.map((teacher, index) => {
      const routeId = getTeacherIdentifier(teacher);
      return {
        queryKey: ["public-tutor-info-detail", routeId || `teacher-${index}`],
        queryFn: () => teacherApi.getTutorDetail(routeId),
        enabled:
          Boolean(routeId) &&
          (!getTeacherYears(teacher).length || !getTeacherEmail(teacher)),
        staleTime: 5 * 60 * 1000,
      };
    }),
  });

  const tutorAvailabilityQueries = useQueries({
    queries: rawTeachers.map((teacher, index) => {
      const routeId = getTeacherIdentifier(teacher);
      return {
        queryKey: ["public-tutor-card-availability", routeId || `teacher-${index}`],
        queryFn: () => teacherApi.getTutorAvailability(routeId),
        enabled: Boolean(routeId),
        retry: false,
        staleTime: 5 * 60 * 1000,
      };
    }),
  });

  const tutors = useMemo(
    () =>
      rawTeachers.map((teacher, index) => {
        const detailTeacher =
          extractTeacherRecord(
            tutorDetailQueries[index]?.data,
            tutorDetailQueries[index]?.data || {}
          ) || tutorDetailQueries[index]?.data;

        const mergedTeacher = detailTeacher ? { ...teacher, ...detailTeacher } : teacher;

        const routeId = getTeacherIdentifier(mergedTeacher);
        const futureAvailabilityCount = mergePendingTutorSlotsFromStorage({
          tutorId: routeId,
          availabilitySlots: normalizeAvailabilityCollection(
            tutorAvailabilityQueries[index]?.data
          ),
        }).filter((slot) => isFutureAvailabilitySlot(slot)).length;

        return buildTutorCardModel(mergedTeacher, index, {
          hasAvailableSlots:
            tutorAvailabilityQueries[index]?.isSuccess && futureAvailabilityCount > 0,
        });
      }),
    [rawTeachers, tutorAvailabilityQueries, tutorDetailQueries]
  );

  const filteredTutors = useMemo(() => tutors, [tutors]);

  const visibleTutors = useMemo(
    () => (showAllTutors ? filteredTutors : filteredTutors.slice(0, 9)),
    [filteredTutors, showAllTutors]
  );

  const handleLogout = () => {
    setShowLogoutDialog(true);
  };

  const handleLogoutConfirm = () => {
    setShowLogoutDialog(false);
    dispatch(logout());
  };

  const handleTutorEnquiryOpen = () => {
    setShowTutorEnquiryPopup(true);
  };

  const handleTutorEnquiryClose = () => {
    setShowTutorEnquiryPopup(false);
  };

  const handleBookFreeTrial = () => {
    if (hasUsedFreeTrial) return;

    markFreeTrialBookingUsed(user);
    setHasUsedFreeTrial(true);
    window.open(FREE_TRIAL_BOOKING_URL, "_blank", "noopener,noreferrer");
  };

  const handleTutorProfileClick = (tutor) => {
    logTutorProfileClicked({
      email: user?.email || null,
      user_name: getUserDisplayName(user),
      user_id: getUserIdentifier(user),
      tutor_id: tutor.routeId || tutor.id,
      tutor_name: tutor.name,
      tutor_email: tutor.email || null,
      tutor_details: {
        price: formatPriceLabel(tutor.price),
        years: tutor.years,
        response_time: tutor.responseTime,
        has_available_slots: tutor.hasAvailableSlots,
      },
    });
  };

  return (
    <div className="tutor-info-page min-h-screen w-full bg-[#f8f3ea] font-inter text-[#1b1b1b]">
      <div className="mx-auto max-w-7xl px-4 pb-12 pt-6 sm:px-6 lg:px-8">
        <header className="border-b border-[#ddd3c6] pb-6">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex items-center">
              <Link className="flex items-center gap-3" to="/tutorinfo">
                <img
                  src={GLOBAL_IMAGES.dingo}
                  alt="MathDingo"
                  className="h-14 w-14 object-contain sm:h-16 sm:w-16"
                />
                <img
                  src={GLOBAL_IMAGES.dingoLogoText}
                  alt="MathDingo"
                  className="h-10 w-auto object-contain sm:h-12"
                />
              </Link>
            </div>

            <div className="flex flex-wrap items-center gap-3 xl:justify-end">
              <Link
                to="/contact"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[#FF5E47] bg-white text-[#FF5E47]"
                aria-label="Contact support"
              >
                <Mail size={18} />
              </Link>
              <button
                type="button"
                onClick={handleTutorEnquiryOpen}
                className="rounded-2xl border border-[#FF5E47] bg-white px-5 py-2.5 text-sm font-medium text-[#FF5E47] sm:text-base"
              >
                Become a Tutor
              </button>
              {token ? (
                <>
                  <Link
                    to="/parent/classBooking/myBookings"
                    className="rounded-2xl bg-[#FF5E47] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#e64d37] sm:text-base"
                  >
                    My Bookings
                  </Link>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="rounded-2xl border border-[#FF5E47] bg-white px-5 py-2.5 text-sm font-medium text-[#FF5E47] transition hover:bg-[#fff4f1] sm:text-base"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/parent/login"
                  className="rounded-2xl bg-[#FF5E47] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#e64d37] sm:text-base"
                >
                  Parent Log In
                </Link>
              )}
            </div>
          </div>
        </header>

        <main className="pt-8">
          <div className="max-w-3xl">
            <span className="inline-flex w-fit max-w-full items-center justify-center rounded-2xl border border-[#f8c8bf] bg-[#fff4f1] px-3 py-1.5 text-center font-inter text-xs font-semibold text-[#ff5a3d] sm:px-4 sm:py-2 sm:text-sm">
              For parents looking for extra maths support
            </span>

            <h1 className="mt-6 font-inter text-3xl font-extrabold leading-tight text-black sm:text-4xl lg:text-5xl">
              Find the Perfect Maths tutor in Australia
            </h1>

            <p
              className="mt-4 max-w-[640px] font-sans text-[16px] font-normal leading-[1.55] text-[#5f6776]"
            >
              Browse experienced tutors, compare pricing and choose a learning
              style that fits your child&apos;s confidence, year level and weekly
              schedule.
            </p>
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            {PUBLIC_FILTER_OPTIONS.map((filter) => (
              <span
                key={filter.id}
                className="inline-flex items-center gap-2 rounded-2xl border border-[#d8cfbf] bg-white px-3 py-1.5 text-center font-inter text-xs font-semibold text-black sm:px-4 sm:py-2 sm:text-sm"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#59c47a] text-white">
                  <Check size={12} strokeWidth={3} />
                </span>
                {filter.label}
              </span>
            ))}
          </div>

          <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p
                className="font-inter text-base text-[#334155] sm:text-lg"
                style={{ fontWeight: 400 }}
              >
                See all our available tutors
              </p>
              <p
                className="mt-2 max-w-[640px] font-sans text-[16px] font-normal leading-[1.55] text-[#5f6776]"
              >
                View response times, teaching levels and clear starting prices to help you choose the right tutor.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              {token ? (
                <button
                  type="button"
                  onClick={handleBookFreeTrial}
                  disabled={hasUsedFreeTrial}
                  className="inline-flex items-center justify-center gap-2 self-start rounded-2xl bg-[#FF5E47] px-6 py-3 text-sm font-medium text-white transition hover:bg-[#e64d37] disabled:cursor-not-allowed disabled:bg-[#cbd5e1] disabled:text-[#475569] sm:text-base"
                >
                  {hasUsedFreeTrial ? "Free Session Booked" : "Book Free Session"}
                  {!hasUsedFreeTrial ? <ArrowRight size={18} /> : null}
                </button>
              ) : (
                <Link
                  to="/parent/signup"
                  className="inline-flex items-center gap-2 self-start rounded-2xl border border-[#FF5E47] bg-white px-6 py-3 text-sm font-medium text-[#FF5E47] transition hover:bg-[#fff4f1] sm:text-base"
                >
                  Create parent account
                  <ArrowRight size={18} />
                </Link>
              )}
            </div>
          </div>

          {teacherListQuery.isLoading && !filteredTutors.length ? (
            <div className="mt-10 rounded-[34px] border border-[#ddd3c6] bg-white px-6 py-12 text-center text-base font-medium text-[#475569] shadow-sm">
              Loading tutor profiles...
            </div>
          ) : teacherListQuery.isError && !filteredTutors.length ? (
            <div className="mt-10 rounded-[34px] border border-[#f1c8bf] bg-[#fff7f5] px-6 py-12 text-center text-base font-medium text-[#a04432] shadow-sm">
              <p>{getPublicTutorListErrorMessage(teacherListQuery.error)}</p>
              <button
                type="button"
                onClick={() => teacherListQuery.refetch()}
                className="mt-5 inline-flex items-center rounded-2xl bg-[#FF5E47] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#e64d37] sm:text-base"
              >
                Retry
              </button>
            </div>
          ) : !filteredTutors.length ? (
            <div className="mt-10 rounded-[34px] border border-[#ddd3c6] bg-white px-6 py-12 text-center text-base font-medium text-[#475569] shadow-sm">
              No tutors matched your current search or filters.
            </div>
          ) : (
            <>
              <section className="mt-10 grid gap-8 md:grid-cols-2 xl:grid-cols-3">
                {visibleTutors.map((tutor) => (
                  <article
                    key={tutor.id}
                    className="flex h-full flex-col rounded-[34px] border border-[#e7edf5] bg-white p-6 shadow-[0_20px_50px_rgba(148,163,184,0.18)]"
                  >
                    <div className="flex items-start gap-4">
                      {tutor.image ? (
                        <img
                          src={tutor.image}
                          alt={tutor.name}
                          className="h-[88px] w-[88px] rounded-full object-cover"
                        />
                      ) : (
                        <div
                          className="flex h-[88px] w-[88px] items-center justify-center rounded-full bg-gradient-to-br from-[#f97316] to-[#ef4444] font-sans text-3xl text-white"
                          style={{ fontWeight: 400 }}
                        >
                          {tutor.initials}
                        </div>
                      )}

                      <div className="min-w-0 flex-1">
                        <h2 className="text-2xl font-medium text-black">
                          {tutor.name}
                        </h2>
                        {tutor.hasAvailableSlots ? (
                          <p className="mt-2 inline-flex rounded-full bg-[#fff4f1] px-3 py-1 text-sm font-medium text-[#FF5E47]">
                            Available
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <div className="mt-5 flex flex-1 flex-col">
                      <p className="max-w-[640px] font-sans text-[16px] font-normal leading-[1.55] text-[#5f6776]">
                        {tutor.description}
                      </p>

                      <div className="mt-auto pt-4 pb-2">
                        <p className="text-sm font-medium uppercase tracking-[0.18em] text-[#64748b]">
                          Year
                        </p>

                        <div className="-ml-2 mt-3 flex flex-wrap items-center gap-2">
                          {tutor.years.length ? (
                            tutor.years.map((year) => (
                              <span
                                key={`${tutor.id}-${year}`}
                                className="rounded-2xl border border-[#ddd3c6] bg-white px-4 py-2 text-sm font-medium text-[#475569]"
                              >
                                {year}
                              </span>
                            ))
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div className="mt-auto flex items-end justify-between gap-4 border-t border-[#efe7da] pt-5">
                      <div>
                        <p className="text-3xl font-medium text-[#111827]">
                          {formatPriceLabel(tutor.price)}
                        </p>
                        <p
                          className="mt-1 font-sans text-[16px] leading-[1.55] text-[#ff6b61]"
                          style={{ fontWeight: 400 }}
                        >
                          {tutor.responseTime}
                        </p>
                      </div>

                      {tutor.routeId ? (
                        <Link
                          to={`/tutorinfo/${tutor.routeId}`}
                          onClick={() => handleTutorProfileClick(tutor)}
                          className="inline-flex items-center gap-2 rounded-2xl bg-[#FF5E47] px-5 py-3 text-sm font-medium text-white transition hover:bg-[#e64d37] sm:text-base"
                        >
                          Profile
                          <ArrowRight size={17} />
                        </Link>
                      ) : (
                        <span className="inline-flex rounded-full border border-[#ddd3c6] px-5 py-3 text-sm font-medium text-[#64748b]">
                          Profile unavailable
                        </span>
                      )}
                    </div>
                  </article>
                ))}
              </section>

              {!showAllTutors && filteredTutors.length > 9 ? (
                <div className="mt-12 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setShowAllTutors(true)}
                    className="w-full max-w-md rounded-[8px] bg-[#FF5E47] px-6 py-3 text-center text-base font-medium text-white transition hover:bg-[#e64d37] sm:text-lg"
                  >
                    Show more tutors
                  </button>
                </div>
              ) : null}
            </>
          )}
        </main>
      </div>
      <LogoutConfirmDialog
        open={showLogoutDialog}
        onCancel={() => setShowLogoutDialog(false)}
        onConfirm={handleLogoutConfirm}
        tone="coral"
      />
      {showTutorEnquiryPopup ? (
        <div className="fixed inset-0 z-[140] flex items-center justify-center bg-[#111827]/40 px-4 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="tutor-enquiry-title"
            className="w-full max-w-md rounded-[32px] border border-[#ddd3c6] bg-white p-6 shadow-[0_24px_70px_rgba(11,43,102,0.18)]"
          >
            <h2
              id="tutor-enquiry-title"
              className="text-center text-3xl font-medium text-[#FF5E47]"
            >
              Become a Teacher
            </h2>
            <p className="mt-4 text-center font-sans text-[16px] font-normal leading-[1.55] text-[#5f6776]">
              In order to become a teacher, please email us at{" "}
              <span className="font-medium text-[#1b1b1b]">
                support@mathdingo.com
              </span>
              .
            </p>

            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={handleTutorEnquiryClose}
                className="inline-flex items-center justify-center rounded-2xl bg-[#FF5E47] px-6 py-3 text-base font-medium text-white transition hover:bg-[#e64d37]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
